// ---------------------------------------------------------------------------
// Merchant Monitoring Events — timeline of monitoring events
// ---------------------------------------------------------------------------

import { StatusBadge } from './StatusBadge';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MonitoringEvent {
  id: string;
  eventType: string;
  severity: string;
  details: string;
  detectedAt: string;
  resolvedAt: string | null;
}

interface MerchantMonitoringEventsProps {
  events: MonitoringEvent[];
  loading?: boolean;
}

// ---------------------------------------------------------------------------
// Event type labels
// ---------------------------------------------------------------------------

const EVENT_TYPE_LABELS: Record<string, string> = {
  chargeback_spike: 'Chargeback Spike',
  volume_anomaly: 'Volume Anomaly',
  compliance_alert: 'Compliance Alert',
  website_change: 'Website Change Detected',
  mcc_mismatch: 'MCC Mismatch',
  sanctions_hit: 'Sanctions Hit',
  adverse_media: 'Adverse Media',
  regulatory_action: 'Regulatory Action',
};

const SEVERITY_COLORS: Record<string, string> = {
  low: 'bg-primary-tint text-primary',
  medium: 'bg-warning-tint text-warning',
  high: 'bg-warning-tint text-warning',
  critical: 'bg-danger-tint text-danger',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MerchantMonitoringEvents({
  events,
  loading = false,
}: MerchantMonitoringEventsProps) {
  const f = useFormatters();
  if (loading) {
    return (
      <div className="rounded-sm border border-hairline bg-surface-raised p-6">
        <h3 className="mb-4 text-sm font-semibold uppercase text-ink-muted">
          Monitoring Events
        </h3>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-sm bg-surface-sunken"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-hairline bg-surface-raised p-6">
      <h3 className="mb-4 text-sm font-semibold uppercase text-ink-muted">
        Monitoring Events
      </h3>

      {events.length === 0 ? (
        <p className="text-sm text-ink-muted">No monitoring events recorded.</p>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div
            className="absolute start-4 top-0 h-full w-0.5 bg-surface-sunken"
            aria-hidden="true"
          />

          <ul className="space-y-4">
            {events.map((event) => (
              <li key={event.id} className="relative flex gap-4 ps-10">
                {/* Timeline dot */}
                <div
                  className={`absolute start-2.5 top-1.5 h-3 w-3 rounded-sm border-2 border-white ${
                    event.resolvedAt ? 'bg-primary' : 'bg-danger'
                  }`}
                  aria-hidden="true"
                />

                <div className="min-w-0 flex-1 rounded-sm border border-hairline bg-surface-sunken p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink">
                        {EVENT_TYPE_LABELS[event.eventType] ?? event.eventType}
                      </p>
                      <p className="mt-0.5 text-xs text-ink-muted">
                        {event.details}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span
                        className={`inline-flex items-center rounded-sm px-2 py-0.5 text-xs font-medium ${
                          SEVERITY_COLORS[event.severity] ??
                          'bg-surface-sunken text-ink'
                        }`}
                      >
                        {event.severity}
                      </span>
                      <StatusBadge
                        value={event.resolvedAt ? 'completed' : 'open'}
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-ink-muted">
                    <span>
                      Detected: {f.date(event.detectedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                    </span>
                    {event.resolvedAt && (
                      <span>
                        Resolved: {f.date(event.resolvedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
